import { useState } from 'react'
import { Button, Group, Modal, Stack, Text, TextInput } from '@mantine/core'
import { useForm } from '@mantine/form'
import { IconCheck, IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { notifications } from 'context'

import { createConnection } from '../service/adminConnectionsService'

interface Props {
  opened: boolean
  onClose: () => void
  onCreated: () => void
}

/**
 * Creates a standalone district connection. The sync itself provisions one
 * account per school afterwards, so no account is picked here.
 */
const NewConnectionModal = ({ opened, onClose, onCreated }: Props) => {
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  const form = useForm({
    initialValues: { districtExternalId: '', districtName: '' },
    validate: {
      districtExternalId: value =>
        value.trim().length === 0 ? t('admin_connections.district_id_required') : null,
    },
  })

  const handleSubmit = form.onSubmit(async values => {
    setSaving(true)
    try {
      await createConnection(values)
      notifications.show({
        message: t('admin_connections.connection_created'),
        color: 'green',
        icon: <IconCheck size={18} />,
      })
      form.reset()
      onCreated()
    } catch {
      notifications.show({
        message: t('admin_connections.error_generic'),
        color: 'red',
        icon: <IconX size={18} />,
      })
    } finally {
      setSaving(false)
    }
  })

  return (
    <Modal opened={opened} onClose={onClose} title={t('admin_connections.new_connection')}>
      <form onSubmit={handleSubmit}>
        <Stack spacing='md'>
          <Text size='sm' color='dimmed'>
            {t('admin_connections.new_connection_hint')}
          </Text>
          <TextInput
            label={t('admin_connections.district_id')}
            placeholder='6a42c7fce00640329e23f65e'
            {...form.getInputProps('districtExternalId')}
          />
          <TextInput
            label={t('admin_connections.district_name')}
            {...form.getInputProps('districtName')}
          />
          <Group position='right'>
            <Button variant='default' onClick={onClose}>
              {t('admin_connections.cancel')}
            </Button>
            <Button type='submit' loading={saving}>
              {t('admin_connections.create_connection')}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  )
}

export default NewConnectionModal
